import { useCallback, useEffect, useState } from 'react';

import { type IFormModalProps } from '@gbs-monorepo-packages/common';

import { ColorPickerSelectData } from '../../../../components/ColorPickerSelectData';
import { type ICoursePage } from '../../../../contexts/coursePage';
import { type ITemplateColors } from '../../../../services/templates';
import { ColorPickerContainer, Container, FormModalCustom } from './styles';
import { useCoursePages } from '../../../../hooks/useCoursePages';

interface IMatchSvgTemplateColorsModalProps
  extends Partial<
    Omit<IFormModalProps, 'children' | 'onAccept' | 'onOpenChange'>
  > {
  loading: boolean;
  colorsDefault: ITemplateColors;
  onAccept: (pages: ICoursePage[]) => Promise<void>;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
  pageId: number;
}

export const MatchSvgTemplateColorsModal = ({
  loading,
  colorsDefault,
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  pageId,
  ...props
}: IMatchSvgTemplateColorsModalProps): JSX.Element | null => {
  const { coursePages } = useCoursePages();

  const [templateColors, setTemplateColors] =
    useState<ITemplateColors>(colorsDefault);

  useEffect(() => {
    const transformedObject: ITemplateColors = {};
    Object.keys(colorsDefault).forEach((key) => {
      transformedObject[key] = colorsDefault[key];
    });

    setTemplateColors(transformedObject);
  }, [colorsDefault]);

  const handleDecline = useCallback(() => {
    onDecline?.();
  }, [onDecline]);

  const handleAccept = useCallback(async () => {
    const coursePagesAux = [...coursePages];
    const updatedCoursePagesAux = coursePagesAux?.map((page) => {
      if (page.id === pageId) {
        return {
          ...page,
          templateColorsFound: templateColors,
        };
      } else {
        return page;
      }
    });

    if (updatedCoursePagesAux) {
      await onAccept(updatedCoursePagesAux);
    }
  }, [templateColors, onAccept, pageId, coursePages]);

  return !open ? null : (
    <FormModalCustom
      hiddenCloseButton
      acceptText="Save"
      dataCy="edit-template-colors-dialog-modal"
      mainText="Match Template Styles"
      disabled={false}
      {...props}
      onAccept={() => {
        void handleAccept();
      }}
      onDecline={handleDecline}
      loading={loading}
      open={!waitToOpen}
    >
      <Container>
        {Object.entries(templateColors).map(([colorKey, colorValue], index) => (
          <ColorPickerContainer key={index}>
            <ColorPickerSelectData
              color={String(colorValue)}
              dataCy={`color-picker-${colorKey}`}
              errorMessage=""
              label={`Import color`}
              onChange={(color, colorKey) => {
                const newTemplateColors: ITemplateColors = {
                  ...templateColors,
                };
                if (colorKey) {
                  newTemplateColors[colorKey] = color;
                  setTemplateColors(newTemplateColors);
                }
              }}
              colorKey={colorKey}
            />
          </ColorPickerContainer>
        ))}
      </Container>
    </FormModalCustom>
  );
};
